import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/contexts/AuthContext';
import AddCustomerModal from './components/AddCustomerModal';
import DeleteConfirmModal from '@/components/base/DeleteConfirmModal';

interface Customer {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  idType: string | null;
  idNumber: string | null;
  customerType: string;
  status: string;
  riskLevel: string;
  profileCompleteness?: number;
  isDeleted: number;
  deletedAt: string | null;
  createdAt: string;
  companyId?: number;
  linkedCompanies?: { id: number; company_name: string }[];
}

const CUSTOMER_TYPE_LABELS: Record<string, string> = {
  individual: 'Individual',
  sole_proprietor: 'Sole Proprietor',
  private_limited: 'Private Limited',
  public_limited: 'Public Limited',
  llp: 'LLP',
  partnership: 'Partnership',
  trust: 'Trust',
  ngo: 'NGO',
  others: 'Others',
};

export default function Customers() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';

  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string; mode: 'soft' | 'hard' } | null>(null);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkTransferOpen, setBulkTransferOpen] = useState(false);
  const [bulkTargetCompanyId, setBulkTargetCompanyId] = useState('');
  const [bulkTransferLoading, setBulkTransferLoading] = useState(false);
  const [bulkTransferError, setBulkTransferError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [activateLoading, setActivateLoading] = useState<number | null>(null);
  const [activateError, setActivateError] = useState<string | null>(null);

  const endpoint = showDeleted ? '/customers?showDeleted=true' : '/customers';
  const { data: customers, loading, refetch } = useApiQuery<Customer[]>({ endpoint });
  const { put, del } = useApi();

  // Fetch companies for bulk transfer dropdown
  const { data: companiesData } = useApiQuery<{ companies?: { id: number; name: string }[] }>({
    endpoint: '/companies',
    enabled: isSuperAdmin,
  });
  const companiesList = companiesData?.companies || [];

  const filtered = customers?.filter((c) =>
    `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.idNumber && c.idNumber.includes(search)) ||
    (c.customerType || '').toLowerCase().includes(search.toLowerCase())
  );

  const filteredByStatus = statusFilter === 'pending_review'
    ? filtered?.filter((c) => c.status !== 'active' && c.status !== 'inactive' && !c.isDeleted)
    : statusFilter === 'all'
      ? filtered
      : filtered?.filter((c) => c.status === statusFilter);

  const riskColors: Record<string, string> = {
    low: 'bg-emerald-50 text-emerald-700',
    medium: 'bg-amber-50 text-amber-700',
    high: 'bg-red-50 text-red-700',
  };

  const typeColors: Record<string, string> = {
    individual: 'bg-sky-50 text-sky-700',
    sole_proprietor: 'bg-violet-50 text-violet-700',
    private_limited: 'bg-teal-50 text-teal-700',
    public_limited: 'bg-orange-50 text-orange-700',
    llp: 'bg-rose-50 text-rose-700',
    partnership: 'bg-indigo-50 text-indigo-700',
    trust: 'bg-amber-50 text-amber-700',
    ngo: 'bg-emerald-50 text-emerald-700',
    others: 'bg-stone-100 text-stone-600',
  };

  const statusColors: Record<string, string> = {
    active: 'bg-emerald-50 text-emerald-700',
    inactive: 'bg-stone-100 text-stone-600',
    under_review: 'bg-amber-50 text-amber-700',
    pending: 'bg-sky-50 text-sky-700',
  };

  const handleSoftDelete = useCallback(async () => {
    if (!deleteTarget || deleteTarget.mode !== 'soft') return;
    await put(`/customers/${deleteTarget.id}/soft-delete`, {});
    setDeleteTarget(null);
    refetch();
  }, [deleteTarget, put, refetch]);

  const handleHardDelete = useCallback(async () => {
    if (!deleteTarget || deleteTarget.mode !== 'hard') return;
    await del(`/customers/${deleteTarget.id}`);
    setDeleteTarget(null);
    refetch();
  }, [deleteTarget, del, refetch]);

  const handleRestore = useCallback(async (id: number) => {
    await put(`/customers/${id}/restore`, {});
    refetch();
  }, [put, refetch]);

  const handleActivate = useCallback(async (id: number) => {
    setActivateLoading(id);
    setActivateError(null);
    try {
      await put(`/customers/${id}/activate`, {});
      refetch();
    } catch (err) {
      setActivateError(err instanceof Error ? err.message : 'Failed to activate');
    } finally {
      setActivateLoading(null);
    }
  }, [put, refetch]);

  // Multi-select handlers
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!filtered) return;
    const visibleIds = filtered.map((c) => c.id);
    const allSelected = visibleIds.every((id) => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkTransfer = useCallback(async () => {
    if (!bulkTargetCompanyId || selectedIds.size === 0) return;
    setBulkTransferLoading(true);
    setBulkTransferError(null);

    try {
      const results = await Promise.all(
        Array.from(selectedIds).map(async (id) => {
          try {
            await put(`/customers/${id}`, { companyId: Number(bulkTargetCompanyId) });
            return { id, ok: true };
          } catch (err) {
            return { id, ok: false, error: err instanceof Error ? err.message : 'Failed' };
          }
        })
      );

      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0) {
        setBulkTransferError(`${failed.length} of ${results.length} transfers failed. Please check permissions.`);
        setBulkTransferLoading(false);
        return;
      }

      setBulkTransferOpen(false);
      setBulkTargetCompanyId('');
      clearSelection();
      refetch();
    } catch (err) {
      setBulkTransferError(err instanceof Error ? err.message : 'Bulk transfer failed');
    } finally {
      setBulkTransferLoading(false);
    }
  }, [bulkTargetCompanyId, selectedIds, put, refetch]);

  const selectedCount = selectedIds.size;

  return (
    <div className="p-4 md:p-8 min-h-screen">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Customers</h1>
          <p className="text-stone-500 text-sm mt-1">Manage customer profiles and KYC status.</p>
        </div>
        <button
          onClick={() => setAddModalOpen(true)}
          className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line" />
          Add Customer
        </button>
      </div>

      {/* Search + Show Deleted Toggle + Status Filter */}
      <div className="bg-white rounded-xl border border-stone-100 p-4 mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="relative max-w-md flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm w-4 h-4 flex items-center justify-center" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, ID number, or type..."
            className="w-full border border-stone-200 rounded-lg pl-10 pr-4 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
          />
        </div>
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
          >
            <option value="all">All Status</option>
            <option value="pending_review">Pending Review</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="under_review">Under Review</option>
            <option value="inactive">Inactive</option>
          </select>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
            />
            <div className="w-9 h-5 bg-stone-300 peer-checked:bg-brand-500 rounded-full transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-4" />
            <span className="text-stone-600 text-sm">Show deleted</span>
          </label>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedCount > 0 && (
        <div className="bg-brand-50 border border-brand-200 rounded-xl px-4 py-3 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-brand-100 text-brand-700 text-xs font-bold flex items-center justify-center">
              {selectedCount}
            </span>
            <span className="text-sm font-medium text-brand-800">
              {selectedCount} customer{selectedCount > 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <button
                onClick={() => {
                  setBulkTransferOpen(true);
                  setBulkTransferError(null);
                  setBulkTargetCompanyId('');
                }}
                className="bg-white border border-brand-200 hover:bg-brand-100 text-brand-700 font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-exchange-line" />
                Transfer Company
              </button>
            )}
            <button
              onClick={clearSelection}
              className="text-stone-500 hover:text-stone-700 text-xs font-medium px-3 py-2 rounded-full hover:bg-stone-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-stone-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <i className="ri-loader-4-line text-brand-600 text-2xl animate-spin w-8 h-8 flex items-center justify-center mx-auto" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-stone-50 border-b border-stone-100">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={!!filteredByStatus && filteredByStatus.length > 0 && filteredByStatus.every((c) => selectedIds.has(c.id))}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-stone-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                    />
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Customer</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">ID Document</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Phone</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Risk Level</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Completeness</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredByStatus?.map((customer) => (
                  <tr
                    key={customer.id}
                    className={`hover:bg-stone-50 transition-colors ${customer.isDeleted ? 'bg-stone-50/60' : ''} ${selectedIds.has(customer.id) ? 'bg-brand-50/40' : ''}`}
                  >
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(customer.id)}
                        onChange={() => toggleSelect(customer.id)}
                        className="w-4 h-4 rounded border-stone-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${customer.isDeleted ? 'bg-stone-200' : 'bg-stone-100'}`}>
                          <span className={`font-bold text-xs ${customer.isDeleted ? 'text-stone-400' : 'text-stone-500'}`}>
                            {customer.firstName.charAt(0)}{customer.lastName.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${customer.isDeleted ? 'text-stone-400 line-through' : 'text-stone-900'}`}>
                            {customer.firstName} {customer.lastName}
                          </p>
                          <p className="text-stone-400 text-xs">{customer.email}</p>
                          {customer.isDeleted && customer.deletedAt && (
                            <p className="text-red-400 text-[10px]">Deleted {new Date(customer.deletedAt).toLocaleDateString()}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeColors[customer.customerType] || 'bg-stone-100 text-stone-600'}`}>
                        {CUSTOMER_TYPE_LABELS[customer.customerType] || customer.customerType || 'Individual'}
                      </span>
                      {customer.linkedCompanies && customer.linkedCompanies.length > 0 && (
                        <p className="text-stone-400 text-[10px] mt-0.5">
                          {customer.linkedCompanies.length} linked compan{customer.linkedCompanies.length > 1 ? 'ies' : 'y'}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className={`text-sm ${customer.isDeleted ? 'text-stone-400' : 'text-stone-700'}`}>{customer.idType || '-'}</p>
                      <p className="text-stone-400 text-xs">{customer.idNumber || '-'}</p>
                    </td>
                    <td className={`px-4 py-3 text-sm ${customer.isDeleted ? 'text-stone-400' : 'text-stone-700'}`}>{customer.phone || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${riskColors[customer.riskLevel] || 'bg-stone-100 text-stone-700'}`}>
                        {customer.riskLevel}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        customer.isDeleted ? 'bg-red-50 text-red-700' :
                        customer.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                      }`}>
                        {customer.isDeleted ? 'Deleted' : customer.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-stone-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              (customer.profileCompleteness ?? 0) >= 80 ? 'bg-emerald-500' : (customer.profileCompleteness ?? 0) >= 50 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(100, customer.profileCompleteness ?? 0)}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-stone-600">{customer.profileCompleteness ?? 0}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/customers/${customer.id}`}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-brand-600 transition-colors cursor-pointer"
                          title="View"
                        >
                          <i className="ri-eye-line text-sm w-4 h-4 flex items-center justify-center" />
                        </Link>
                        {!customer.isDeleted && (
                          <button
                            onClick={() => setDeleteTarget({ id: customer.id, name: `${customer.firstName} ${customer.lastName}`, mode: 'soft' })}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-red-600 transition-colors cursor-pointer"
                            title="Deactivate"
                          >
                            <i className="ri-delete-bin-line text-sm w-4 h-4 flex items-center justify-center" />
                          </button>
                        )}
                        {customer.isDeleted && (
                          <>
                            <button
                              onClick={() => handleRestore(customer.id)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-emerald-50 text-stone-400 hover:text-emerald-600 transition-colors cursor-pointer"
                              title="Reactivate"
                            >
                              <i className="ri-arrow-go-back-line text-sm w-4 h-4 flex items-center justify-center" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget({ id: customer.id, name: `${customer.firstName} ${customer.lastName}`, mode: 'hard' })}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-600 transition-colors cursor-pointer"
                              title="Permanently delete"
                            >
                              <i className="ri-fire-line text-sm w-4 h-4 flex items-center justify-center" />
                            </button>
                          </>
                        )}
                        {isSuperAdmin && !customer.isDeleted && customer.status !== 'active' && (
                          <button
                            onClick={() => handleActivate(customer.id)}
                            disabled={activateLoading === customer.id}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-emerald-50 text-stone-400 hover:text-emerald-600 transition-colors cursor-pointer"
                            title="Activate"
                          >
                            {activateLoading === customer.id ? (
                              <i className="ri-loader-4-line animate-spin text-sm w-4 h-4 flex items-center justify-center" />
                            ) : (
                              <i className="ri-check-line text-sm w-4 h-4 flex items-center justify-center" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredByStatus?.length === 0 && (
              <div className="p-8 text-center">
                <i className="ri-user-line text-stone-300 text-3xl mb-2 w-12 h-12 flex items-center justify-center mx-auto" />
                <p className="text-stone-400 text-sm">
                  {showDeleted ? 'No deleted customers found.' : statusFilter === 'pending_review' ? 'No customers pending review.' : 'No customers found.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <AddCustomerModal open={addModalOpen} onClose={() => setAddModalOpen(false)} onSuccess={refetch} />

      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={deleteTarget?.mode === 'hard' ? handleHardDelete : handleSoftDelete}
        title={deleteTarget?.mode === 'hard' ? 'Permanent Delete' : 'Deactivate Customer'}
        entityName={deleteTarget?.name}
        description={
          deleteTarget?.mode === 'hard'
            ? 'This will permanently erase the customer profile, all KYC requests, and every uploaded document from disk. This action cannot be undone.'
            : 'The customer profile will be disabled and hidden from the list. You can reactivate it later.'
        }
      />

      {/* Bulk Transfer Company Modal */}
      {bulkTransferOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={() => setBulkTransferOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
              <div>
                <h2 className="text-lg font-bold text-stone-900">Transfer Customers</h2>
                <p className="text-stone-400 text-xs mt-0.5">Move {selectedCount} selected customer{selectedCount > 1 ? 's' : ''} to another company</p>
              </div>
              <button
                onClick={() => setBulkTransferOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {bulkTransferError && (
                <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2">
                  <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center" />
                  {bulkTransferError}
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Target Company *</label>
                <select
                  value={bulkTargetCompanyId}
                  onChange={(e) => setBulkTargetCompanyId(e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                >
                  <option value="">Choose a company...</option>
                  {companiesList.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <p className="text-stone-400 text-xs leading-relaxed">
                <i className="ri-information-line text-brand-600 mr-1" />
                This will reassign all selected customers to the chosen company. Their documents and KYC history will remain intact.
              </p>
              <div className="pt-2 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setBulkTransferOpen(false)}
                  className="px-4 py-2.5 rounded-full text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleBulkTransfer}
                  disabled={!bulkTargetCompanyId || bulkTransferLoading}
                  className="bg-brand-600 hover:bg-brand-700 disabled:bg-stone-300 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  {bulkTransferLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Transferring...
                    </>
                  ) : (
                    <>
                      <i className="ri-exchange-line" />
                      Transfer {selectedCount} Customer{selectedCount > 1 ? 's' : ''}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
